// ============================================================================
// REGISTRATION STATUS (ADR-0048 §1 amended) — the first thing on Tax Settings →
// Registrations: is the entity registered for tax under its jurisdiction, from
// when, and the history of changes. Entity-level: a user logs into a unit, but
// registration belongs to the entity; units differ only in which registration
// they issue under (the links on the list below this card).
//
//   Fresh entity  →  "Yes, from <date>"  |  "No, below the threshold" (no date: from the start)
//   Registered    →  "Registered since <date> · Change…" (deregister from a date)
//   Not registered→  "Not registered since <date> · Change…" (register from a date)
//   Latest row    →  correct its date or undo it while no document sits on or after it
// ============================================================================

import React, { useState } from 'react';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { taxRegistrationStatusService } from 'api/services';
import type { TaxRegistrationStatusDto, TaxRegistrationStatus } from 'api/types';

const fmt = (d?: string | null) => (d ? d.slice(0, 10) : '');
const today = () => new Date().toISOString().slice(0, 10);

export interface RegistrationStatusCardProps {
  jurisdictionName: string;
  info: TaxRegistrationStatusDto | null;
  canEdit: boolean;
  /** Called after any change so the screen reloads the timeline and the readiness badge. */
  onChanged: (next: TaxRegistrationStatusDto) => void;
  /** One line about the unit the user is working in, e.g. "Maun issues under 29ABCDE1234F1Z5". */
  unitCue?: string | null;
}

const RegistrationStatusCard: React.FC<RegistrationStatusCardProps> = ({ jurisdictionName, info, canEdit, onChanged, unitCue }) => {
  const [busy, setBusy] = useState(false);
  const [yesDate, setYesDate] = useState(today());
  const [change, setChange] = useState<{ status: TaxRegistrationStatus; date: string; note: string } | null>(null);
  const [correct, setCorrect] = useState<string | null>(null);
  const [undoOpen, setUndoOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const run = async (work: () => Promise<TaxRegistrationStatusDto>, done: string) => {
    setBusy(true);
    try {
      const next = await work();
      onChanged(next);
      window.dispatchEvent(new Event('fbp:setup-changed'));
      notificationService.toast.success(done);
      setChange(null); setCorrect(null); setUndoOpen(false);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not save the registration status.');
    } finally { setBusy(false); }
  };

  const latest = info?.latest ?? null;
  const current = info?.currentStatus ?? null;
  const box: React.CSSProperties = {
    border: '1px solid var(--border-color)', borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1rem',
    background: current === 'REGISTERED' ? 'var(--success-bg, #f0fdf4)' : current === 'NOT_REGISTERED' ? 'var(--bg-secondary, #f8fafc)' : 'var(--warning-bg, #fef3c7)',
  };

  // ---- Fresh entity: the question -------------------------------------------------------
  if (!latest) {
    return (
      <div style={box}>
        <div style={{ fontWeight: 700, marginBottom: '0.35rem' }}>Is this entity registered for {jurisdictionName}?</div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
          Registration belongs to the entity and applies to every business unit. A business below the threshold answers <em>No</em>: documents charge no tax,
          ask no tax questions and need no tax ledgers. Invoicing stays closed until this is answered.
        </div>
        <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <label className="form-label" style={{ margin: 0 }}>Registered from</label>
            <input type="date" className="form-input" style={{ width: 170 }} value={yesDate} onChange={e => setYesDate(e.target.value)} disabled={!canEdit || busy} />
            <button className="btn btn-primary btn-sm" disabled={!canEdit || busy || !yesDate}
              onClick={() => run(() => taxRegistrationStatusService.set({ status: 'REGISTERED', effectiveFrom: yesDate }), `Registered for ${jurisdictionName} from ${yesDate}.`)}>
              Yes, registered
            </button>
          </div>
          <button className="btn btn-secondary btn-sm" disabled={!canEdit || busy}
            onClick={() => run(() => taxRegistrationStatusService.set({ status: 'NOT_REGISTERED' }), 'Recorded as not registered: documents charge no tax.')}>
            No, below the threshold
          </button>
        </div>
      </div>
    );
  }

  // ---- Answered: current status, change, history ----------------------------------------
  const opposite: TaxRegistrationStatus = latest.status === 'REGISTERED' ? 'NOT_REGISTERED' : 'REGISTERED';
  const upcoming = latest.effectiveFrom.slice(0, 10) > today() ? latest : null;
  return (
    <div style={box}>
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ fontWeight: 700 }}>
            {current === 'REGISTERED' ? `Registered for ${jurisdictionName}` : current === 'NOT_REGISTERED' ? `Not registered for ${jurisdictionName}` : `No status in force yet for ${jurisdictionName}`}
            {info?.currentFrom && <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}> since {fmt(info.currentFrom)}</span>}
          </div>
          {upcoming && (
            <div style={{ fontSize: '0.8rem', color: 'var(--warning-text, #b45309)' }}>
              Changes to <strong>{upcoming.status === 'REGISTERED' ? 'registered' : 'not registered'}</strong> from {fmt(upcoming.effectiveFrom)}.
            </div>
          )}
          {current === 'NOT_REGISTERED' && !upcoming && (
            <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Documents charge no tax (out of scope); no registrations or tax ledgers are needed. The Tax Summary still reports supplies.</div>
          )}
          {unitCue && <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 4 }}>{unitCue}</div>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" disabled={!canEdit || busy}
            onClick={() => setChange({ status: opposite, date: today(), note: '' })}>
            {opposite === 'REGISTERED' ? 'Register from a date…' : 'Deregister from a date…'}
          </button>
          {!info?.latestLocked && (
            <>
              <button className="btn btn-link btn-sm" disabled={!canEdit || busy} onClick={() => setCorrect(fmt(latest.effectiveFrom))} title="Correct the date of the latest change">Correct date</button>
              <button className="btn btn-link btn-sm text-danger" disabled={!canEdit || busy} onClick={() => setUndoOpen(true)} title="Remove the latest change">Undo</button>
            </>
          )}
          {(info?.history.length ?? 0) > 1 && (
            <button className="btn btn-link btn-sm" onClick={() => setShowHistory(v => !v)}>{showHistory ? 'Hide history' : 'History'}</button>
          )}
        </div>
      </div>

      {info?.latestLocked && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
          Documents are posted on or after {fmt(latest.effectiveFrom)}, so that change is history; a correction is a new change from a later date.
        </div>
      )}

      {showHistory && (
        <table style={{ width: '100%', fontSize: '0.8rem', marginTop: '0.75rem', borderCollapse: 'collapse' }}>
          <thead><tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}><th style={{ padding: '0.3rem' }}>From</th><th style={{ padding: '0.3rem' }}>Status</th><th style={{ padding: '0.3rem' }}>Note</th><th style={{ padding: '0.3rem' }}>Recorded by</th></tr></thead>
          <tbody>
            {[...(info?.history ?? [])].reverse().map(h => (
              <tr key={h.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                <td style={{ padding: '0.3rem', whiteSpace: 'nowrap' }}>{fmt(h.effectiveFrom)}</td>
                <td style={{ padding: '0.3rem' }}>{h.status === 'REGISTERED' ? 'Registered' : 'Not registered'}</td>
                <td style={{ padding: '0.3rem', color: 'var(--text-secondary)' }}>{h.note || ''}</td>
                <td style={{ padding: '0.3rem', color: 'var(--text-muted)' }}>{h.createdByName || ''}{h.createdAt ? ` · ${fmt(h.createdAt)}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {change && (
        <div style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px dashed var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-primary, #fff)' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
            {change.status === 'REGISTERED' ? 'Register the entity' : 'Deregister the entity'} for {jurisdictionName}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label className="form-label">{change.status === 'REGISTERED' ? 'Registered from' : 'Not registered from'} <span className="text-danger">*</span></label>
              <input type="date" className="form-input" style={{ width: 170 }} value={change.date} min={fmt(latest.effectiveFrom)} onChange={e => setChange({ ...change, date: e.target.value })} />
            </div>
            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 220 }}>
              <label className="form-label">Note</label>
              <input className="form-input" value={change.note} maxLength={300} placeholder="Registration granted / below the threshold…" onChange={e => setChange({ ...change, note: e.target.value })} />
            </div>
            <button className="btn btn-primary btn-sm" disabled={busy || !change.date}
              onClick={() => run(() => taxRegistrationStatusService.set({ status: change.status, effectiveFrom: change.date, note: change.note || null }),
                `${change.status === 'REGISTERED' ? 'Registered' : 'Not registered'} from ${change.date}.`)}>
              Save
            </button>
            <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setChange(null)}>Cancel</button>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
            Applies to the whole entity from that date. Documents dated before it keep the earlier status.
          </div>
        </div>
      )}

      {correct !== null && (
        <div style={{ marginTop: '0.75rem', padding: '0.75rem', border: '1px dashed var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-primary, #fff)', display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label className="form-label">Correct the date of the latest change</label>
            <input type="date" className="form-input" style={{ width: 170 }} value={correct} onChange={e => setCorrect(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-sm" disabled={busy || !correct}
            onClick={() => run(() => taxRegistrationStatusService.correctLatestDate({ effectiveFrom: correct, rowVersion: latest.rowVersion }), `Date corrected to ${correct}.`)}>
            Save
          </button>
          <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setCorrect(null)}>Cancel</button>
        </div>
      )}

      <ConfirmDialog
        open={undoOpen}
        title="Remove the latest status change?"
        message={`This removes "${latest.status === 'REGISTERED' ? 'Registered' : 'Not registered'} from ${fmt(latest.effectiveFrom)}". ${(info?.history.length ?? 0) > 1 ? 'The previous status applies again.' : 'The entity will have no status until you answer again.'}`}
        confirmLabel="Remove"
        cancelLabel="Keep"
        variant="danger"
        loading={busy}
        loadingMessage="Removing…"
        onConfirm={() => run(() => taxRegistrationStatusService.deleteLatest(latest.rowVersion), 'Latest status change removed.')}
        onCancel={() => setUndoOpen(false)}
      />
    </div>
  );
};

export default RegistrationStatusCard;
